import { fetchReport } from '../../../services/reportsApi';
import type { EodSummaryReportPayload } from '../../../interface/eodClickhouseInterface';

const REPORTS_BASE_URL = process.env.REACT_APP_API_ENDPOINT_REPORTS || '';

/**
 * In-flight request de-duplication (EODCH-API-AUDIT).
 *
 * Two EOD sections read this one endpoint — the Summary Report table and the Funds
 * table — each with its own page / size / sort, which is why they are two calls and
 * not one. But their defaults coincide exactly (page 1, size 10, sortBy
 * `businessDate`, sortAsc true), so a plain page load fired the SAME request twice,
 * back to back, for one response's worth of data.
 *
 * Keyed on the fully-built URL plus the token, so requests only collapse when they
 * are genuinely identical; the moment either table is sorted or paged the keys
 * diverge and both calls go out as before. The entry is dropped as soon as the
 * promise settles, so this is a concurrency guard and never a response cache — a
 * refetch after the first has finished still hits the network.
 */
const inFlight = new Map<string, Promise<any>>();

const dedupe = (key: string, run: () => Promise<any>): Promise<any> => {
  const pending = inFlight.get(key);
  if (pending) return pending;
  const p = run().finally(() => {
    if (inFlight.get(key) === p) inFlight.delete(key);
  });
  inFlight.set(key, p);
  return p;
};

/**
 * GET /sales/live/v2/eodSummaryReport
 *   ?locationId=&startDate=&endDate=&page=&size=&sortBy=&sortAsc=
 *
 * ClickHouse replacement for the legacy `/eod/getEODReports`. Server-paginated
 * and server-sorted like the endpoint it replaces.
 *
 * `startDate` / `endDate` are appended only when present, so the live day sends
 * `locationId` alone — the same convention every other ClickHouse KPI follows.
 * `page` is ONE-based on the wire (callers hold a zero-based page and add 1).
 * Omitting `sortAsc` lets the backend default to ascending.
 */
export const getEodSummaryReportApi = ({
  locationId,
  startDate,
  endDate,
  page,
  size,
  sortBy,
  sortAsc,
  token,
}: EodSummaryReportPayload) => {
  const params = new URLSearchParams({ locationId });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (page != null) params.set('page', String(page));
  if (size != null) params.set('size', String(size));
  if (sortBy) params.set('sortBy', sortBy);
  if (sortAsc != null) params.set('sortAsc', String(sortAsc));
  const url = `${REPORTS_BASE_URL}/sales/live/v2/eodSummaryReport?${params}`;
  return dedupe(`${url}|${token ?? ''}`, () => fetchReport(url, token));
};
